// TeamInsightsPage — combined Team Insights + Suggested Actions panels.
//
// Decision authority: companyAdmin OR any user granted
// suggestionsRun / teamInsightsView. The Run-now button is gated on
// suggestionsRun (server-side); the rest of the page is read-only.
//
// Data: GET /suggestions?projectId=
//       POST /suggestions/run?projectId=
//       DELETE /suggestions/:id
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Empty, Skeleton, Tag, Select, message, Tooltip } from "antd";
import { CloseOutlined, StarOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import useLastProject from "components/Coordination/useLastProject";
import "./TeamInsights.css";

function auth() { return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` }; }

async function fetchSuggestions(projectId) {
  const res = await fetch(`${url}/suggestions?projectId=${projectId}`, { headers: auth() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
async function runPass(projectId) {
  const res = await fetch(`${url}/suggestions/run?projectId=${projectId}`, {
    method: "POST", headers: auth(),
  });
  if (!res.ok) throw new Error((await res.json())?.err || `HTTP ${res.status}`);
  return res.json();
}
async function dismissOne(id) {
  const res = await fetch(`${url}/suggestions/${id}`, { method: "DELETE", headers: auth() });
  return res.ok;
}
async function fetchProjects() {
  const res = await fetch(`${url}/projects/picker`, { headers: auth() });
  if (!res.ok) return [];
  const body = await res.json();
  return Array.isArray(body) ? body : (body?.projects || []);
}

const TEAM_PRIORITIES   = ["star", "support", "review"];
const ACTION_PRIORITIES = ["urgent", "recommended", "next"];
const PRIORITY_LABEL = {
  star: "Star contributors", support: "Needs support", review: "Performance review needed",
  urgent: "Urgent", recommended: "Highly recommended", next: "Next likely action",
};
const PRIORITY_COLOR = {
  star: "green", support: "orange", review: "red",
  urgent: "red", recommended: "orange", next: "blue",
};

function ScoreRing({ score, color, size = 34 }) {
  if (score == null) return null;
  const r = 22, c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, score / 100));
  const stroke = color === "red" ? "#DC2626"
              : color === "orange" ? "#D97706"
              : color === "green" ? "#16A34A"
              : "#3B82F6";
  return (
    <svg viewBox="0 0 60 60" style={{ width: size, height: size, flexShrink: 0 }}>
      <circle cx="30" cy="30" r={r} stroke="#EFEFEF" strokeWidth="7" fill="none" />
      <circle cx="30" cy="30" r={r} stroke={stroke} strokeWidth="7" fill="none"
              strokeDasharray={`${c * pct} ${c}`}
              transform="rotate(-90 30 30)" strokeLinecap="round" />
      <text x="30" y="35" textAnchor="middle" fontSize="17" fontWeight="600" fill="#23343E">{score}</text>
    </svg>
  );
}

// One compact tile per suggestion: ring + name/role on one line, the
// first reasons as short lines (the rest behind a tooltip), actions as
// small links. Tiles pack into a fluid grid instead of one tall column
// per priority, so thirty people fit on a screen.
const VISIBLE_REASONS = 3;
function SuggestionTile({ s, onDismiss, allowDismiss, onNavigate }) {
  const color = PRIORITY_COLOR[s.priority] || "blue";
  const subject = s.subjectUserId
    ? `${s.subjectUserId.name || ""} ${s.subjectUserId.lastName || ""}`.trim()
    : s.title;
  const sub = s.subjectUserId ? (s.subjectUserId.role || s.body || "") : (s.body || "");
  const reasons = s.reasons || [];
  const hidden = reasons.slice(VISIBLE_REASONS);
  return (
    <div className={`ti-tile ti-tile--${color}`}>
      <div className="ti-tile__head">
        {s.score != null && <ScoreRing score={s.score} color={color} />}
        <div className="ti-tile__who">
          <div className="ti-tile__name" title={subject}>{subject}</div>
          {sub && <div className="ti-tile__sub" title={sub}>{sub}</div>}
        </div>
        {allowDismiss && (
          <Tooltip title="Dismiss">
            <button type="button" className="ti-tile__dismiss" onClick={() => onDismiss(s._id)} aria-label="Dismiss">
              <CloseOutlined />
            </button>
          </Tooltip>
        )}
      </div>
      {reasons.length > 0 && (
        <ul className="ti-tile__reasons">
          {reasons.slice(0, VISIBLE_REASONS).map((r, i) => <li key={i} title={r}>{r}</li>)}
          {hidden.length > 0 && (
            <li className="ti-tile__more">
              <Tooltip title={<ul style={{ margin: 0, paddingLeft: 16 }}>{hidden.map((r, i) => <li key={i}>{r}</li>)}</ul>}>
                <span>+{hidden.length} more</span>
              </Tooltip>
            </li>
          )}
        </ul>
      )}
      {s.actions && s.actions.length > 0 && (
        <div className="ti-tile__actions">
          {s.actions.map((a, i) => (
            a.href ? (
              <a key={i}
                 href={a.href.startsWith("/") ? a.href : a.href}
                 onClick={(e) => { if (a.href.startsWith("/")) { e.preventDefault(); onNavigate(a.href); } }}>
                {a.label}
              </a>
            ) : <span key={i} className="is-disabled">{a.label}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// A priority group: one header line, then the fluid tile grid. Empty
// groups render nothing here - the chip row above already says "0".
function Group({ priority, list, allowDismiss, onDismiss, onNavigate }) {
  if (!list.length) return null;
  return (
    <div className="ti-group">
      <div className="ti-group__title">
        <Tag color={PRIORITY_COLOR[priority]} style={{ margin: 0 }}>{PRIORITY_LABEL[priority]}</Tag>
        <span className="ti-group__count">{list.length}</span>
      </div>
      <div className="ti-grid">
        {list.map(s => (
          <SuggestionTile key={s._id} s={s}
                          onDismiss={onDismiss} allowDismiss={allowDismiss} onNavigate={onNavigate} />
        ))}
      </div>
    </div>
  );
}

// Exported as a section so the Analytics & Performance page can embed the server-side
// PSI insights below its activity grid (lossless merge — the standalone
// /team-insights route keeps rendering the same component via the
// default export wrapper at the bottom of this file).
// `projectId` / `projects` make the section controlled (the Analytics &
// Performance page drives it from its own filter bar); without them it
// keeps its own picker and the remembered coordination project.
export function TeamInsightsSection({ embedded = false, projectId: projectIdProp, projects: projectsProp, onProjectChange }) {
  const history = useHistory();
  const me = useSelector(s => s?.user?.userInfo) || {};
  const userPerms = useSelector(s => s?.user?.userPermission) || [];
  const companyPerms = useSelector(s => s?.user?.companyPermission) || [];
  // Same intersection the sidebar uses.
  const finalPerms = new Set([...(userPerms || []), ...(companyPerms || [])]);
  const canRun = !!(me.admin || me.companyAdmin
    || finalPerms.has(ALL_PERMISSIONS.SUGGESTIONS_RUN));
  const canDismiss = !!(me.admin || me.companyAdmin
    || finalPerms.has(ALL_PERMISSIONS.SUGGESTIONS_DISMISS));

  const controlled = projectIdProp !== undefined;
  const [ownProjects, setOwnProjects] = useState([]);
  const projects = controlled && projectsProp ? projectsProp : ownProjects;
  // Remembers the last project (shared coordination memory); ?projectId= wins.
  const [ownProjectId, setOwnProjectId] = useLastProject(projects);
  const projectId = controlled ? (projectIdProp || "") : ownProjectId;
  const setProjectId = controlled ? (onProjectChange || (() => {})) : setOwnProjectId;
  // Chip filter: click a priority chip to show only that group.
  const [only, setOnly] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (controlled) return;
    fetchProjects().then(ps => {
      setOwnProjects(ps);
      // Fall back to the first project when nothing (valid) is remembered.
      if ((!projectId || !ps.some((p) => p._id === projectId)) && ps[0]?._id) setProjectId(ps[0]._id);
    });
  }, []); // eslint-disable-line

  // Load the stored suggestions; when the project was never scored (or its
  // batch is stale - older than a day) and the viewer may run the pass,
  // run it first so the page never sits empty.
  const load = async (pid, { autoRun = true } = {}) => {
    if (!pid) return;
    setLoading(true);
    try {
      let rows = await fetchSuggestions(pid);
      const newest = rows.reduce((m, r) => Math.max(m, new Date(r.generatedAt || r.createdAt || 0).getTime()), 0);
      const stale = !rows.length || (Date.now() - newest) > 24 * 3600 * 1000;
      if (autoRun && canRun && stale) {
        try { await runPass(pid); rows = await fetchSuggestions(pid); } catch (e) { /* keep what we have */ }
      }
      setData(rows);
    } catch (e) { message.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load(projectId);
    if (projectId && !controlled) {
      const sp = new URLSearchParams(window.location.search);
      sp.set("projectId", projectId);
      history.replace(`${window.location.pathname}?${sp.toString()}`);
    }
  }, [projectId, history]); // eslint-disable-line

  const onRun = async () => {
    setRunning(true);
    try { await runPass(projectId); load(projectId); message.success("Scoring pass complete"); }
    catch (e) { message.error(e.message); }
    finally { setRunning(false); }
  };
  const onDismiss = async (id) => {
    const ok = await dismissOne(id);
    if (ok) setData(d => d.filter(x => x._id !== id));
  };

  const ALL = [...TEAM_PRIORITIES, ...ACTION_PRIORITIES];
  const counts = Object.fromEntries(ALL.map(p => [p, data.filter(s => s.priority === p).length]));
  const shown = only ? [only] : ALL;
  const projectTitle = projects.find(p => p._id === projectId)?.title;

  return (
    <div className="ti" style={{ padding: embedded ? 0 : 24 }}>
      {!embedded && <CoordinationBreadcrumbs current="Team Insights" />}
      <div className="ti-head">
        <div className="ti-head__text">
          {embedded ? (
            <h3 style={{ margin: 0 }}>Team Insights <Tag color="purple">BETA</Tag></h3>
          ) : (
            <h2 style={{ margin: 0 }}>Team Insights <Tag color="purple">BETA</Tag></h2>
          )}
          <div className="ti-head__sub">
            Scored PSI (problem solver index) from the scoring pass
            {controlled && projectTitle ? <> · <strong>{projectTitle}</strong></> : null}
            {embedded ? " - treat scores as prompts for conversation." : ". Decision authority: companyAdmin - treat scores as prompts for conversation."}
          </div>
        </div>
        <div className="ti-head__tools">
          {!controlled && (
            <Select style={{ width: 260 }} value={projectId || undefined} onChange={(v) => setProjectId(v || "")}
                    showSearch allowClear placeholder="Project (type to search)" size="small"
                    filterOption={(input, option) => String(option?.label || "").toLowerCase().includes(input.toLowerCase())}
                    options={projects.map(p => ({ label: p.title, value: p._id }))} />
          )}
          {canRun && (
            <Button type="primary" size="small" loading={running} onClick={onRun} disabled={!projectId}>
              Run scoring pass
            </Button>
          )}
        </div>
      </div>

      {/* Priority chips: the whole picture in one line; click to focus a group. */}
      {!loading && data.length > 0 && (
        <div className="ti-chips">
          {ALL.map((p, i) => (
            <React.Fragment key={p}>
              {i === TEAM_PRIORITIES.length && <span className="ti-chips__sep" />}
              <button type="button"
                      className={`ti-chip ti-chip--${PRIORITY_COLOR[p]}${only === p ? " is-active" : ""}${counts[p] ? "" : " is-empty"}`}
                      onClick={() => setOnly(only === p ? null : p)}>
                {i >= TEAM_PRIORITIES.length && <StarOutlined style={{ marginRight: 4 }} />}
                {PRIORITY_LABEL[p]} <b>{counts[p]}</b>
              </button>
            </React.Fragment>
          ))}
        </div>
      )}

      {loading && <Skeleton active paragraph={{ rows: 4 }} />}

      {!loading && shown.map(p => (
        <Group key={p} priority={p} list={data.filter(s => s.priority === p)}
               allowDismiss={canDismiss} onDismiss={onDismiss} onNavigate={(h) => history.push(h)} />
      ))}

      {!loading && data.length === 0 && (
        <Empty description={
          !projectId ? "Pick a project to see its team insights."
          : canRun
            ? "No suggestions yet. Click 'Run scoring pass' to generate them."
            : "No suggestions yet. Ask an admin to run the scoring pass."
        } />
      )}
    </div>
  );
}

export default function TeamInsightsPage() {
  return <TeamInsightsSection />;
}
